import usePaymentOrderOptions, { mergePaymentOrders } from "./usePaymentOrderOptions";
import usePaymentFormChoices from "./usePaymentFormChoices";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  Layout,
  Table,
  Button,
  Input,
  DatePicker,
  Select,
  Modal,
  Form,
  InputNumber,
  Checkbox,
  Space,
  message,
  Row,
  Col,
  Card,
  Typography,
  Spin,
  Tag,
  Alert,
  TableProps,
  Collapse,
  Empty,
  theme,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  LockOutlined,
  ReloadOutlined,
  SaveOutlined,
  CheckCircleFilled,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useSelection } from "../SelectionContext";
import axios from "axios";
import dayjs from "dayjs";
import locale from "dayjs/locale/fr";
import "dayjs/locale/fr";
import * as XLSX from "xlsx";
// @ts-ignore - Context from JS file
import { useAuth } from "../context/AuthContext";
import API_CONFIG from '../config/api.js';
import ThemeContext from "../context/ThemeContext";
import "./payments-workspace.css";
import RowActionsMenu from "../components/RowActionsMenu";
import { useCommercialTreasuryAccountSuggestion } from "../hooks/useCommercialTreasurySuggestion";
import {
  buildPaymentSupplierOptions,
  type PaymentSupplierOption,
} from "./paymentEntityOptions";

// Configuration d'axios
axios.defaults.baseURL = API_CONFIG.BASE_URL;

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

dayjs.locale(locale);


type Supplier = PaymentSupplierOption;


interface Order {
  id: number;
  invoice_number: string;
  order_date: string;
  total: number;
  paid_amount: number;
  due_amount: number;
  payment_status: string;
  selected?: boolean;
  payment_amount?: number;
  supplier_name?: string;
}

interface Payment {
  id: number;
  payment_number: string;
  date: string;
  amount: number;
  payment_mode_name: string;
  entity_name: string;
  warehouse_name: string;
  company_name: string;
  treasury_account_id?: number;
  online_order_id?: number | null;
  ecommerce_order_number?: string | null;
  ecommerce_payment_event?: "capture" | "refund" | null;
}

interface PaymentSummary {
  amount: number;
  payment_count: number;
  customer_count: number;
  by_mode: { payment_mode_id: number | null; name: string; amount: number; count: number }[];
}

const money = (value: unknown) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
  ? `${Number(value).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} FCFA`
  : "—";

// Définir le composant avec un type explicite
const PaymentSortant = (): React.ReactNode => {
  const { selectedWarehouse, selectedCompany } = useSelection();
  const { token } = theme.useToken();
  const appearance = useContext(ThemeContext) as { isDarkMode?: boolean } | null;
  const workspaceStyle = {
    "--ip-bg": token.colorBgLayout,
    "--ip-surface": token.colorBgContainer,
    "--ip-text": token.colorText,
    "--ip-muted": token.colorTextSecondary,
    "--ip-heading": appearance?.isDarkMode ? token.colorTextHeading : token.colorPrimaryTextActive,
    "--ip-accent": appearance?.isDarkMode ? token.colorPrimaryText : token.colorPrimary,
    "--ip-tint": token.colorPrimaryBg,
    "--ip-fill": token.colorFillAlter,
    "--ip-line": token.colorBorderSecondary,
    "--ip-success": token.colorSuccessText,
    "--ip-warning": token.colorWarningText,
  } as React.CSSProperties;
  const [loading, setLoading] = useState<boolean>(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [remarksExpanded, setRemarksExpanded] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [unpaidOrders, setUnpaidOrders] = useState<Order[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [currentSupplierName, setCurrentSupplierName] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const paymentPage = pagination.current;
  const paymentPageSize = pagination.pageSize;
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [currentPayment, setCurrentPayment] = useState<any>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [form] = Form.useForm();
  // Nouvel état pour les paiements sélectionnés
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<number[]>([]);
  // État local pour gérer l'ID du magasin sélectionné
  const [currentWarehouseId, setCurrentWarehouseId] = useState<number | null>(
    null
  );
  // @ts-ignore - Context from JS file
  const { user, hasPermission } = useAuth();
  const canViewPayments = hasPermission("Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.view");
  const disbursedAmount = Number(Form.useWatch("amount", form) || 0);
  const formEntityId = Form.useWatch("supplier_id", form);
  const formChoices = usePaymentFormChoices({ userId: user?.id, companyId: selectedCompany,
    warehouseId: currentWarehouseId, paymentType: "out", active: modalVisible,
    enabled: hasPermission(`Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.${editMode ? "edit" : "create"}`) });
  const balances = usePaymentOrderOptions({ userId: user?.id, companyId: selectedCompany,
    warehouseId: currentWarehouseId, paymentType: "out", entityId: formEntityId,
    editingPaymentId: editMode ? currentPayment?.id : null, active: modalVisible,
    enabled: hasPermission(`Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.${editMode ? "edit" : "create"}`) });
  const treasuryAccounts = formChoices.treasuryAccounts;
  const accountsLoading = formChoices.loading;
  const paymentModes = formChoices.paymentModes;
  const loadingPaymentModes = formChoices.loading;
  const formEntities = buildPaymentSupplierOptions(formChoices.entities);
  const savingPayment = useRef(false);
  const listRequest = useRef<{ id: number; controller?: AbortController }>({ id: 0 });
  const activeScope = useRef("");
  activeScope.current = `${user?.id}:${selectedCompany}:${currentWarehouseId}`;
  const exportFilterScope = useRef("");
  exportFilterScope.current = JSON.stringify([activeScope.current, searchText, selectedSupplier,
    dateRange?.[0]?.format("YYYY-MM-DD"), dateRange?.[1]?.format("YYYY-MM-DD")]);
  const previousContext = useRef(`${user?.id}:${selectedCompany}:${currentWarehouseId}`);
  useEffect(() => {
    const context = `${user?.id}:${selectedCompany}:${currentWarehouseId}`;
    if (previousContext.current !== context) {
      previousContext.current = context;
      setModalVisible(false);
      setUnpaidOrders([]);
      setSelectedSupplier(null);
      setCurrentSupplierName("");
      setSelectedPaymentIds([]);
      setDetailModalVisible(false);
      setCurrentPayment(null);
      form.resetFields();
    }
  }, [user?.id, selectedCompany, currentWarehouseId, form]);
  useEffect(() => {
    if (modalVisible && !formEntityId && !editMode) setUnpaidOrders([]);
    else if (modalVisible && balances.data) {
      setUnpaidOrders((previous) => mergePaymentOrders(balances.data!.orders, previous));
    }
  }, [modalVisible, formEntityId, balances.data, editMode]);

  const { onModeChange: suggestTreasuryAccount } =
    useCommercialTreasuryAccountSuggestion(form, {
      warehouseId: currentWarehouseId,
      enabled: modalVisible,
      modeField: "payment_mode_id",
      accountField: "treasury_account_id",
      accounts: treasuryAccounts,
    });

  // Gérer le changement de magasin sélectionné
  useEffect(() => {
    // Extraire l'ID
    const selectedId = selectedWarehouse?.id || selectedWarehouse;
    const currentId = currentWarehouseId;

    // Comparer les IDs pour éviter les boucles infinies
    if (String(selectedId) !== String(currentId)) {
      setCurrentWarehouseId(selectedId ? Number(selectedId) : null);
      // Réinitialiser la pagination à 1 pour montrer les nouveaux résultats depuis le début
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, [selectedWarehouse, currentWarehouseId]);

  // Charger les paiements
  const fetchPayments = useCallback(async () => {
    listRequest.current.controller?.abort();
    const controller = new AbortController();
    const requestId = listRequest.current.id + 1;
    listRequest.current = { id: requestId, controller };
    const requestedScope = `${user?.id}:${selectedCompany}:${currentWarehouseId}`;
    setSummary(null);
    setPayments([]);
    setSelectedPaymentIds([]);
    setListError(null);
    // Ne pas faire d'appel API si aucun magasin n'est sélectionné
    if (!currentWarehouseId || !canViewPayments) {
      setListLoading(false);
      setPagination((prev) => ({ ...prev, total: 0 }));
      return;
    }

    setListLoading(true);
    try {
      const params: any = {
        payment_type: "out",
        page: paymentPage,
        limit: paymentPageSize,
        company_id: selectedCompany,
        include_summary: "1",
      };


      if (currentWarehouseId) {
        params.warehouse_id = currentWarehouseId;
      }

      if (searchText) {
        params.search = searchText;
      }

      if (selectedSupplier) {
        params.supplier_id = selectedSupplier;
      }

      if (dateRange && dateRange[0] && dateRange[1]) {
        params.date_from = dateRange[0].format("YYYY-MM-DD");
        params.date_to = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get("/api/payments", { params, signal: controller.signal,
        headers: { "X-Company-Id": selectedCompany } });
      if (controller.signal.aborted || listRequest.current.id !== requestId || activeScope.current !== requestedScope) return;

      // The API provides the supplier name for the scoped journal.
      const paymentsData = response.data.payments || response.data || [];
      if (!Array.isArray(paymentsData)) throw new Error("Réponse des paiements invalide.");
      const totals = response.data.summary;
      setSummary(totals && Array.isArray(totals.by_mode)
        && [totals.amount, totals.payment_count, totals.customer_count].every((value) => typeof value === "number" && Number.isFinite(value))
        && totals.by_mode.every((mode: any) => typeof mode.amount === "number" && typeof mode.count === "number"
          && Number.isFinite(mode.amount) && Number.isFinite(mode.count)) ? totals : null);

      setPayments(paymentsData);

      setPagination((prev) => ({
        ...prev,
        total: Number(response.data.total ?? paymentsData.length),
      }));
    } catch (error) {
      if (controller.signal.aborted || listRequest.current.id !== requestId || activeScope.current !== requestedScope) return;
      setListError("Impossible de charger les paiements. Vos filtres sont conservés.");
    } finally {
      if (listRequest.current.id === requestId && !controller.signal.aborted) setListLoading(false);
    }
  }, [currentWarehouseId, selectedCompany, user?.id, canViewPayments, paymentPage, paymentPageSize, searchText, selectedSupplier, dateRange]);

  // Charger les fournisseurs
  const fetchSuppliers = useCallback(async () => {
    const requestedScope = `${user?.id}:${selectedCompany}:${currentWarehouseId}`;
    // Ne pas charger les fournisseurs si aucun magasin n'est sélectionné
    if (!currentWarehouseId || !canViewPayments) {
      setSuppliers([]);
      return;
    }

    try {
      const apiUrl = "/api/users/suppliers";
      const params: any = {};

      if (currentWarehouseId) {
        params.warehouseId = currentWarehouseId;
      }
      if (selectedCompany) {
        params.companyId = selectedCompany;
      }

      const response = await axios.get(apiUrl, { params, headers: { "X-Company-Id": selectedCompany } });
      if (activeScope.current !== requestedScope) return;

      const supplierRows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.suppliers)
          ? response.data.suppliers
          : null;

      if (supplierRows) {
        setSuppliers(buildPaymentSupplierOptions(supplierRows));
      } else {
        console.error(
          "Format de données inattendu pour les fournisseurs:",
          response.data
        );
        message.error("Erreur: format de données des fournisseurs incorrect");

        // Tentative alternative avec une API différente en cas d'échec
        try {
          const altResponse = await axios.get("/api/suppliers", { params, headers: { "X-Company-Id": selectedCompany } });
          if (activeScope.current !== requestedScope) return;

          if (Array.isArray(altResponse.data)) {
            setSuppliers(buildPaymentSupplierOptions(altResponse.data));
          }
        } catch (altError) {
          console.error("Échec de l'API alternative:", altError);
        }
      }
    } catch (error) {
      if (activeScope.current !== requestedScope) return;
      console.error("Erreur lors du chargement des fournisseurs:", error);
      message.error("Erreur lors du chargement des fournisseurs");
    }
  }, [currentWarehouseId, selectedCompany, user?.id, canViewPayments]);

  // Charger les détails d'un paiement
  const fetchPaymentDetails = useCallback(async (paymentId: number) => {
    if (!canViewPayments) return;
    const requestedScope = activeScope.current;
    setLoading(true);
    try {
      const response = await axios.get(`/api/payments/${paymentId}`);
      if (activeScope.current !== requestedScope) return;
      setCurrentPayment(response.data);
      setDetailModalVisible(true);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails du paiement:",
        error
      );
      message.error("Erreur lors du chargement des détails du paiement");
    } finally {
      setLoading(false);
    }
  }, [canViewPayments]);

  // Effet initial pour charger les données
  useEffect(() => {
    void fetchPayments();
    return () => listRequest.current.controller?.abort();
  }, [fetchPayments]);
  useEffect(() => { void fetchSuppliers(); }, [fetchSuppliers]);

  // Vérifier si l'URL contient un payment_id et, si c'est le cas, ouvrir les détails du paiement
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const paymentId = queryParams.get("payment_id");

    if (paymentId) {
      fetchPaymentDetails(Number(paymentId));
    }
  }, [fetchPaymentDetails]);

  // Gérer le changement de pagination
  const handleTableChange = (pagination: any) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  // Gérer la recherche
  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  // Gérer le changement de fournisseur
  const handleSupplierChange = (value: number | undefined) => {
    // Si la valeur est undefined, c'est que l'utilisateur a effacé le filtre
    if (value === undefined) {
      setSelectedSupplier(null); // Utiliser null au lieu de undefined
      setPagination({ ...pagination, current: 1 });
      return;
    }

    // Utiliser directement la valeur (USER_ID) pour le filtrage
    setSelectedSupplier(value);
    setPagination({ ...pagination, current: 1 });
  };

  // Gérer le changement de date
  const handleDateChange = (dates: any) => {
    setDateRange(dates);
    setPagination({ ...pagination, current: 1 });
  };

  // Ouvrir le modal pour ajouter un paiement
  const openAddModal = () => {
    if (!currentWarehouseId) {
      Modal.warning({
        title: "Établissement non sélectionné",
        content:
          "Veuillez sélectionner un établissement avant de créer ou modifier un paiement.",
      });
      return;
    }
    setEditMode(false);
    setRemarksExpanded(false);
    form.resetFields();

    // Recharger les modes de paiement pour le magasin actuel

    // Ajouter des valeurs par défaut pour éviter l'avertissement "form not connected"
    form.setFieldsValue({
      payment_date: dayjs(),
      amount: 0,
      supplier_id: undefined,
      payment_mode_id: undefined,
      treasury_account_id: undefined,
      notes: "",
    });

    setUnpaidOrders([]);
    setCurrentSupplierName("");
    setModalVisible(true);
  };

  // Ouvrir le modal pour éditer un paiement
  const openEditModal = async (record: Payment) => {
    const openingScope = activeScope.current;
    if (record.online_order_id) {
      Modal.info({
        title: "Remboursement e-commerce protégé",
        content:
          "Cette écriture est pilotée depuis la commande en ligne et ne peut pas être modifiée directement.",
      });
      return;
    }
    setEditMode(true);
    setLoading(true);
    try {
      const response = await axios.get(`/api/payments/${record.id}`);
      if (activeScope.current !== openingScope) return;
      const payment = response.data;
      setCurrentPayment(payment);
      setCurrentSupplierName(payment.entity_name || suppliers.find((supplier) => supplier.USER_ID === Number(payment.entity_id || payment.user_id))?.Nom_Raison_Sociale || "");
      setRemarksExpanded(Boolean(payment.notes));
      // Préremplir le formulaire
      form.setFieldsValue({
        supplier_id: payment.entity_id || payment.user_id,
        payment_date: dayjs(payment.date),
        payment_mode_id: payment.payment_mode_id,
        treasury_account_id: payment.treasury_account_id || undefined,
        amount: payment.amount,
        notes: payment.notes,
      });

      setUnpaidOrders((payment.orders || []).map((op: any) => ({
        id: Number(op.order_id), invoice_number: op.invoice_number || "N/A",
        order_date: op.order_date || "", total: Number(op.order_total || 0),
        paid_amount: Number(op.order_paid_amount || 0),
        due_amount: Number(op.order_due_amount || 0),
        payment_status: op.order_payment_status || "", selected: true,
        payment_amount: Number(op.amount_applied ?? op.amount ?? 0),
      })));

      setModalVisible(true);
    } catch (error) {
      console.error("Erreur lors du chargement du paiement:", error);
      message.error("Erreur lors du chargement du paiement");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSupplierChange = (value: number | undefined) => {
    form.setFieldsValue({ supplier_id: value });
    setUnpaidOrders([]);
    setCurrentSupplierName(formEntities.find((item) => item.USER_ID === value)?.Nom_Raison_Sociale || "");
  };

  // Gérer la sélection d'une commande
  const handleOrderSelection = (orderId: number, checked: boolean) => {
    const updatedOrders = [...unpaidOrders];
    const index = updatedOrders.findIndex((o) => o.id === orderId);
    if (index !== -1) {
      updatedOrders[index].selected = checked;
      if (checked && !updatedOrders[index].payment_amount) {
        updatedOrders[index].payment_amount = updatedOrders[index].due_amount;
      }
      setUnpaidOrders(updatedOrders);
    }
  };

  // Gérer le changement de montant pour une commande
  const handleOrderAmountChange = (orderId: number, amount: number) => {
    const updatedOrders = [...unpaidOrders];
    const index = updatedOrders.findIndex((o) => o.id === orderId);
    if (index !== -1) {
      updatedOrders[index].payment_amount = amount;
      setUnpaidOrders(updatedOrders);
    }
  };

  // Calculer le montant total des commandes sélectionnées
  const calculateTotalSelectedAmount = () => {
    return unpaidOrders
      .filter((o) => o.selected)
      .reduce((sum, order) => sum + (order.payment_amount || 0), 0);
  };

  // Soumettre le formulaire
  const handleSubmit = async (values: any) => {
    // Récupérer les commandes sélectionnées
    const selectedOrders = unpaidOrders
      .filter((o) => o.selected)
      .map((o) => ({
        order_id: o.id,
        amount: o.payment_amount || 0,
      }));

    // Vérifier si des commandes sont sélectionnées
    if (selectedOrders.length === 0 && !editMode) {
      Modal.confirm({
        title: "Aucune commande sélectionnée",
        content:
          "Vous n'avez sélectionné aucune commande à régler. Voulez-vous quand même enregistrer ce paiement sans l'affecter à des commandes?",
        okText: "Oui, continuer",
        cancelText: "Non, revenir",
        onOk: () => processPayment(values, selectedOrders),
      });
      return;
    }

    // Calcul du total des montants sélectionnés
    const totalSelectedAmount = calculateTotalSelectedAmount();

    // Vérifier la cohérence entre le montant saisi et le total des commandes
    if (Math.abs(totalSelectedAmount - values.amount) > 1 && !editMode) {
      // Tolérance de 1 pour les erreurs d'arrondi
      Modal.confirm({
        title: "Montants différents",
        content: `Le montant total que vous avez saisi (${values.amount.toLocaleString(
          "fr-FR"
        )} XOF) est différent du total des montants affectés aux commandes (${totalSelectedAmount.toLocaleString(
          "fr-FR"
        )} XOF). Voulez-vous continuer quand même?`,
        okText: "Oui, continuer",
        cancelText: "Non, revenir",
        onOk: () => processPayment(values, selectedOrders),
      });
    } else {
      // Si tout est cohérent, procéder directement
      processPayment(values, selectedOrders);
    }
  };

  // Fonction pour traiter le paiement après validation
  const processPayment = async (values: any, selectedOrders: any[]) => {
    if (savingPayment.current) return;
    savingPayment.current = true;
    setLoading(true);
    // --- Récupérer les ID depuis le contexte ---
    const currentCompanyId = selectedCompany;
    const warehouseIdToUse = currentWarehouseId;

    // --- Validation Stricte des IDs ---
    if (!currentCompanyId || typeof currentCompanyId !== "number") {
      message.error(
        "Erreur critique : ID d'entreprise invalide ou manquant. Veuillez rafraîchir la page ou re-sélectionner."
      );
      setLoading(false);
      console.error(
        "[processPayment] Invalid or missing company ID from context:",
        currentCompanyId
      );
      savingPayment.current = false;
      return;
    }
    if (!warehouseIdToUse || typeof warehouseIdToUse !== "number") {
      message.error(
        "Erreur critique : ID d'établissement invalide ou manquant. Veuillez rafraîchir la page ou re-sélectionner."
      );
      setLoading(false);
      console.error(
        "[processPayment] Invalid or missing warehouse ID from context:",
        warehouseIdToUse
      );
      savingPayment.current = false;
      return;
    }
    // --- Fin Validation Stricte ---

    try {
      // Le USER_ID du fournisseur est directement dans values.supplier_id
      const userIdToUse = values.supplier_id;
      const supplierName =
        suppliers.find((s) => s.USER_ID === userIdToUse)?.Nom_Raison_Sociale ||
        "Inconnu";

      if (!userIdToUse || isNaN(Number(userIdToUse))) {
        console.error("USER_ID invalide:", userIdToUse);
        message.error(
          `Erreur: Identifiant utilisateur manquant ou invalide pour le fournisseur "${supplierName}". Contactez l'administrateur.`
        );
        setLoading(false);
        return;
      }

      await balances.verify(selectedOrders);

      // --- Préparer les données du paiement SANS FALLBACKS DANGEREUX ---
      const paymentData = {
        company_id: currentCompanyId, // Utiliser l'ID de l'entreprise du contexte
        warehouse_id: warehouseIdToUse, // Utiliser l'ID du magasin du contexte
        payment_type: "out",
        date: values.payment_date.format("YYYY-MM-DD"),
        amount: values.amount,
        payment_mode_id: values.payment_mode_id,
        treasury_account_id: values.treasury_account_id,
        user_id: userIdToUse, // Utiliser uniquement USER_ID
        notes: values.notes,
        orders: selectedOrders,
        staff_user_id: user?.id, // Ajouter l'ID de l'utilisateur connecté
      };

      // --- Appel API ---
      if (editMode && currentPayment) {
        await axios.put(`/api/payments/${currentPayment.id}`, paymentData);
        message.success("Paiement mis à jour avec succès");
      } else {
        await axios.post("/api/payments", paymentData);
        message.success("Paiement créé avec succès");
      }

      void balances.invalidate();
      setModalVisible(false);
      fetchPayments(); // Recharger la liste
    } catch (error: any) {
      if (error.response?.data?.code === "ORDER_PAYMENT_BALANCE_CHANGED") {
        void balances.refresh().catch(() => {});
      }
      console.error("[processPayment] Error saving payment:", error);
      // Afficher une erreur plus détaillée si possible
      const errorMsg =
        error.response?.data?.error || error.message || "Erreur inconnue";
      message.error(`Erreur lors de l'enregistrement: ${errorMsg}`);
    } finally {
      savingPayment.current = false;
      setLoading(false);
    }
  };

  // Supprimer un paiement
  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer ce paiement ?",
      content:
        "Cette action est irréversible. Les montants payés et les statuts des commandes associées à ce paiement seront également mis à jour.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: async () => {
        setLoading(true);
        try {
          await axios.delete(`/api/payments/${id}`);
          void balances.invalidate();
          message.success("Paiement supprimé avec succès");
          fetchPayments();
          // Réinitialiser les paiements sélectionnés après suppression
          setSelectedPaymentIds([]);
        } catch (error) {
          console.error("Erreur lors de la suppression du paiement:", error);
          message.error("Erreur lors de la suppression du paiement");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Nouvelle fonction pour gérer la suppression multiple
  const handleMultipleDelete = () => {
    if (selectedPaymentIds.length === 0) return;

    Modal.confirm({
      title: `Êtes-vous sûr de vouloir supprimer ${selectedPaymentIds.length} paiement(s) ?`,
      content:
        "Cette action est irréversible. Les montants payés et les statuts des commandes associées à ces paiements seront également mis à jour.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: async () => {
        setLoading(true);
        try {
          // Supprimer chaque paiement sélectionné
          const deletePromises = selectedPaymentIds.map((id) =>
            axios.delete(`/api/payments/${id}`).then((response) => {
              void balances.invalidate();
              return response;
            })
          );

          await Promise.all(deletePromises);

          message.success(
            `${selectedPaymentIds.length} paiement(s) supprimé(s) avec succès`
          );
          fetchPayments();
          // Réinitialiser les paiements sélectionnés après suppression
          setSelectedPaymentIds([]);
        } catch (error) {
          console.error("Erreur lors de la suppression des paiements:", error);
          message.error("Erreur lors de la suppression des paiements");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Nouvelle fonction pour gérer la sélection d'un paiement
  const handlePaymentSelection = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedPaymentIds((prev) => [...prev, id]);
    } else {
      setSelectedPaymentIds((prev) =>
        prev.filter((paymentId) => paymentId !== id)
      );
    }
  };

  // Nouvelle fonction pour gérer la sélection de tous les paiements
  const handleSelectAllPayments = (checked: boolean) => {
    if (checked) {
      const allIds = payments
        .filter((payment) => !payment.online_order_id)
        .map((payment) => payment.id);
      setSelectedPaymentIds(allIds);
    } else {
      setSelectedPaymentIds([]);
    }
  };

  const handleExportToExcel = async () => {
    if (!currentWarehouseId || !canViewPayments) return;
    const exportScope = activeScope.current;
    const exportedFilters = exportFilterScope.current;
    if (!(await ensureSubscriptionActionAllowed("data_export"))) return;
    if (activeScope.current !== exportScope || exportFilterScope.current !== exportedFilters) return;
    setExportLoading(true);
    message.loading({ content: "Préparation de l’export Excel…", key: "exportLoading" });
    try {
      const params: any = { payment_type: "out", company_id: selectedCompany,
        warehouse_id: currentWarehouseId, limit: 200 };
      if (searchText) params.search = searchText;
      if (selectedSupplier) params.supplier_id = selectedSupplier;
      if (dateRange?.[0] && dateRange?.[1]) {
        params.date_from = dateRange[0].format("YYYY-MM-DD");
        params.date_to = dateRange[1].format("YYYY-MM-DD");
      }
      const rows = new Map<number, Payment>();
      let expectedTotal: number | null = null;
      for (let page = 1; ; page += 1) {
        const response = await axios.get("/api/payments", { params: { ...params, page },
          headers: { "X-Company-Id": selectedCompany } });
        if (activeScope.current !== exportScope || exportFilterScope.current !== exportedFilters) {
          throw new Error("Le contexte ou les filtres ont changé. Relancez l’export.");
        }
        const batch = response.data.payments;
        const total = Number(response.data.total);
        if (!Array.isArray(batch) || !Number.isInteger(total) || total < 0) {
          throw new Error("Réponse d’export invalide. Aucun fichier n’a été créé.");
        }
        if (expectedTotal === null) expectedTotal = total;
        if (expectedTotal !== total || (batch.length === 0 && rows.size < total)) {
          throw new Error("La liste a changé pendant l’export. Relancez l’export pour obtenir un fichier complet.");
        }
        for (const payment of batch) {
          if (rows.has(payment.id)) throw new Error("La liste a changé pendant l’export. Relancez l’export.");
          rows.set(payment.id, payment);
        }
        if (rows.size === expectedTotal) break;
        if (rows.size > expectedTotal) throw new Error("La liste a changé pendant l’export. Relancez l’export.");
      }
      const formattedData = Array.from(rows.values()).map((payment) => ({
        Date: dayjs(payment.date).format("DD/MM/YYYY"), "No Transaction": payment.payment_number,
        Fournisseur: payment.entity_name, Établissement: payment.warehouse_name,
        "Mode de paiement": payment.payment_mode_name, Montant: payment.amount,
        Devise: "XOF", Entreprise: payment.company_name,
      }));
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      worksheet["!cols"] = [12, 22, 30, 30, 20, 18, 10, 24].map((wch) => ({ wch }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Paiements Sortants");
      XLSX.writeFile(workbook, "Paiements_Sortants_" + dayjs().format("YYYY-MM-DD") + ".xlsx");
      message.success({ content: "Export Excel complet : " + rows.size + " paiement(s).", key: "exportLoading" });
    } catch (error: any) {
      message.error({ content: error.message || "Erreur lors de l’export Excel", key: "exportLoading" });
    } finally {
      setExportLoading(false);
    }
  };

  const establishmentName = (typeof selectedWarehouse === "object" && selectedWarehouse?.name)
    || payments[0]?.warehouse_name || "Établissement sélectionné";
  const companyName = payments[0]?.company_name;
  const allocatedAmount = calculateTotalSelectedAmount();
  const allocationGap = disbursedAmount - allocatedAmount;
  const selectedOrderCount = unpaidOrders.filter((order) => order.selected).length;
  const periodLabel = dateRange?.[0] && dateRange?.[1]
    ? "Du " + dateRange[0].format("DD/MM/YYYY") + " au " + dateRange[1].format("DD/MM/YYYY")
    : "Toutes les périodes";
  const amountInputProps = {
    formatter: (value: any) => value ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "",
    parser: (value: any) => value ? Number(value.replace(/\s/g, "")) : 0,
  };
  const orderColumns: TableProps<Order>["columns"] = [
    { title: <Checkbox aria-label="Sélectionner toutes les commandes"
        checked={unpaidOrders.length > 0 && unpaidOrders.every((order) => order.selected)}
        indeterminate={selectedOrderCount > 0 && selectedOrderCount < unpaidOrders.length}
        onChange={(event) => setUnpaidOrders((previous) => previous.map((order) => ({ ...order,
          selected: event.target.checked,
          payment_amount: event.target.checked ? (order.payment_amount || order.due_amount) : order.payment_amount,
        })))} />, key: "selection", width: 44,
      render: (_, order) => <Checkbox checked={order.selected}
        aria-label={"Sélectionner la commande " + order.invoice_number}
        onChange={(event) => handleOrderSelection(order.id, event.target.checked)} /> },
    { title: "N° de facture", dataIndex: "invoice_number", key: "invoice", width: 150,
      render: (value, order) => <div className="payment-workspace__reference">{value}
        <span className="payment-workspace__small-date">{dayjs(order.order_date).format("DD/MM/YYYY")}</span></div> },
    { title: "Date", dataIndex: "order_date", key: "date", width: 110, responsive: ["lg"],
      render: (value) => dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY") : "—" },
    { title: "Statut", dataIndex: "payment_status", key: "status", width: 175, responsive: ["md"],
      render: (value) => <Tag color={String(value).toLowerCase().includes("non") ? "red" : "orange"}
        icon={<ExclamationCircleOutlined />}>{value || "Non défini"}</Tag> },
    { title: "Total", dataIndex: "total", key: "total", align: "right", width: 140,
      responsive: ["lg"], render: money },
    { title: "Montant dû", dataIndex: "due_amount", key: "due", align: "right", width: 140,
      render: money },
    { title: "À affecter (FCFA)", key: "allocation", width: 180, align: "right",
      render: (_, order) => <InputNumber {...amountInputProps} min={0}
        disabled={!order.selected} value={order.payment_amount}
        aria-label={"Montant affecté à " + order.invoice_number}
        onChange={(value) => handleOrderAmountChange(order.id, Number(value || 0))} /> },
  ];
  const selectablePayments = payments.filter((payment) => !payment.online_order_id);
  const columns: TableProps<Payment>["columns"] = [
    { title: <Checkbox aria-label="Sélectionner tous les paiements de la page"
        onChange={(event) => handleSelectAllPayments(event.target.checked)}
        checked={selectablePayments.length > 0 && selectedPaymentIds.length === selectablePayments.length}
        indeterminate={selectedPaymentIds.length > 0 && selectedPaymentIds.length < selectablePayments.length} />,
      key: "selection", width: 44,
      render: (_, record) => <Checkbox checked={selectedPaymentIds.includes(record.id)}
        aria-label={"Sélectionner le paiement " + record.payment_number}
        disabled={Boolean(record.online_order_id)}
        onChange={(event) => handlePaymentSelection(record.id, event.target.checked)} /> },
    { title: "Date", dataIndex: "date", key: "date", width: 112, responsive: ["lg"],
      render: (value) => dayjs(value).format("DD/MM/YYYY") },
    { title: "N° de transaction", dataIndex: "payment_number", key: "reference", width: 190,
      render: (value, record) => <div className="payment-workspace__reference">{value}
        <span className="payment-workspace__small-date">{dayjs(record.date).format("DD/MM/YYYY")}</span>
        {record.online_order_id && <Tag color="blue">Remboursement e-commerce · {record.ecommerce_order_number}</Tag>}</div> },
    { title: "Fournisseur", dataIndex: "entity_name", key: "supplier", ellipsis: true },
    { title: "Établissement", dataIndex: "warehouse_name", key: "warehouse", responsive: ["lg"],
      ellipsis: true, className: "payment-workspace__muted" },
    { title: "Mode de paiement", dataIndex: "payment_mode_name", key: "mode", width: 150 },
    { title: "Montant", dataIndex: "amount", key: "amount", width: 165, align: "right",
      className: "payment-workspace__amount", render: money },
    { title: "Actions", key: "actions", width: 100, align: "center",
      render: (_, record) => {
        const protectedPayment = Boolean(record.online_order_id);
        const actionItems: any[] = [];
        if (hasPermission("Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.edit") && !protectedPayment) {
          actionItems.push({ key: "edit", icon: <EditOutlined aria-hidden="true" />, label: "Modifier",
            onClick: () => openEditModal(record) });
        }
        if (hasPermission("Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.delete") && !protectedPayment) {
          if (actionItems.length) actionItems.push({ type: "divider" });
          actionItems.push({ key: "delete", icon: <DeleteOutlined aria-hidden="true" />, label: "Supprimer", danger: true,
            onClick: () => handleDelete(record.id) });
        }
        if (protectedPayment) actionItems.push({ key: "ecommerce-info", icon: <LockOutlined />,
          label: "Piloté par " + (record.ecommerce_order_number || "la commande e-commerce"), disabled: true });
        return <Space size={0}>
          {canViewPayments && <Button type="text" icon={<EyeOutlined />}
            aria-label={"Voir le détail du paiement " + record.payment_number}
            onClick={() => fetchPaymentDetails(record.id)} />}
          {actionItems.length > 0 && <RowActionsMenu items={actionItems}
            ariaLabel={"Afficher les actions du paiement " + record.payment_number} />}
        </Space>;
      } },
  ];

  // Vérifier si l'utilisateur a la permission de voir le module
  if (!hasPermission("Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.view")) {
    return (
      <Layout className="site-layout">
        <Content style={{ margin: "0 16px" }}>
          <div style={{ padding: 24, minHeight: 360 }}>
            <Title level={2}>Paiements Sortants</Title>
            <p>
              Vous n'avez pas les permissions nécessaires pour voir cette
              section.
            </p>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="payment-workspace" style={workspaceStyle}>
      <Content>
        <div className="payment-workspace__workspace">
          <header className="payment-workspace__header">
            <div><Title level={2}>Paiements sortants</Title>
              <p>Une lecture claire des règlements versés</p></div>
            <Space wrap className="payment-workspace__actions">
              <Button icon={<FileExcelOutlined aria-hidden="true" />} aria-label="Exporter sur Excel" onClick={handleExportToExcel}
                loading={exportLoading} disabled={!currentWarehouseId || listLoading || Boolean(listError) || !pagination.total}>
                Exporter sur Excel</Button>
              {hasPermission("Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.create") &&
                <Button type="primary" icon={<PlusOutlined aria-hidden="true" />} aria-label="Nouveau paiement" onClick={openAddModal}
                  disabled={!currentWarehouseId}>Nouveau paiement</Button>}
            </Space>
          </header>

          {!currentWarehouseId ? <Alert type="info" showIcon
            message="Sélection d’établissement requise"
            description="Sélectionnez un établissement dans le menu en haut à droite pour consulter et gérer ses paiements sortants." /> : <>
            <section className="payment-workspace__overview" aria-label="Synthèse des décaissements" aria-busy={listLoading}>
              <div className="payment-workspace__period"><CalendarOutlined /> {periodLabel}</div>
              <div className="payment-workspace__metrics">
                <div className="payment-workspace__metric payment-workspace__metric--total">
                  <strong>{listLoading ? "Chargement…" : summary ? money(summary.amount) : "—"}</strong>
                  <span>Total des paiements filtrés</span>
                </div>
                {!listLoading && summary?.by_mode.map((mode) => <div className="payment-workspace__metric"
                  key={mode.payment_mode_id ?? "unknown"}>
                  <strong>{money(mode.amount)}</strong>
                  <span>{mode.name} · {mode.count.toLocaleString("fr-FR")} paiement{mode.count > 1 ? "s" : ""}</span>
                </div>)}
              </div>
              {!listLoading && !summary && !listError && <Text type="secondary">Synthèse indisponible pour cette liste.</Text>}
            </section>

            <section className="payment-workspace__filters" aria-label="Filtres des paiements sortants">
              <div><label htmlFor="outgoing-transaction-search">N° de transaction</label>
                <Input id="outgoing-transaction-search" prefix={<SearchOutlined />} allowClear value={searchText}
                  placeholder="Rechercher une transaction" onChange={(event) => handleSearch(event.target.value)} /></div>
              <div><label htmlFor="outgoing-supplier-filter">Fournisseur</label>
                <Select id="outgoing-supplier-filter" placeholder="Tous les fournisseurs" allowClear showSearch
                  optionFilterProp="children" value={selectedSupplier} onChange={handleSupplierChange}>
                  {suppliers.map((supplier) => <Option key={supplier.ID_Frs} value={supplier.USER_ID}>{supplier.Nom_Raison_Sociale}</Option>)}
                </Select></div>
              <div><label htmlFor="outgoing-period-start">Période</label>
                <RangePicker id={{ start: "outgoing-period-start", end: "outgoing-period-end" }}
                  format="DD/MM/YYYY" placeholder={["Date de début", "Date de fin"]}
                  value={dateRange} onChange={handleDateChange} /></div>
            </section>

            {listError && <Alert type="error" showIcon message="Paiements indisponibles" description={listError}
              action={<Button onClick={() => void fetchPayments()}>Réessayer</Button>} />}
            <section className="payment-workspace__journal" aria-labelledby="outgoing-payment-journal-title">
              <div className="payment-workspace__journal-heading">
                <h3 id="outgoing-payment-journal-title">Journal des paiements</h3>
                <span>{listLoading ? "Chargement…" : listError ? "Résultats indisponibles"
                  : pagination.total.toLocaleString("fr-FR") + " paiement" + (pagination.total > 1 ? "s" : "")
                    + (summary ? " · " + summary.customer_count.toLocaleString("fr-FR") + " fournisseur" + (summary.customer_count > 1 ? "s" : "") + " identifié" + (summary.customer_count > 1 ? "s" : "") : "")}</span>
              </div>
              {selectedPaymentIds.length > 0 && hasPermission("Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.delete") &&
                <div className="payment-workspace__selection"><span>{selectedPaymentIds.length} paiement(s) sélectionné(s)</span>
                  <Button danger icon={<DeleteOutlined />} onClick={handleMultipleDelete}>Supprimer la sélection</Button></div>}
              <Table columns={columns} dataSource={payments} rowKey="id" loading={listLoading}
                tableLayout="fixed" scroll={{ x: 760 }} onChange={handleTableChange}
                locale={{ emptyText: listError ? "La liste n’a pas pu être chargée." : "Aucun paiement pour ces filtres." }}
                pagination={{ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
                  showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40", "50"],
                  showTotal: (total, range) => total ? "Affichage de " + range[0] + " à " + range[1] + " sur " + total + " paiements" : "0 paiement" }} />
            </section>
          </>}

          <Modal className="payment-entry-modal" open={modalVisible} centered
            title={<div className="payment-entry-modal__heading"><div>
              <Title level={3}>{editMode ? "Modifier le paiement" : "Nouveau paiement"}</Title>
              <p>Renseignez le règlement, puis choisissez les commandes à régler.</p></div>
              <span className="payment-entry-modal__context"><BankOutlined /> {companyName ? companyName + " · " : ""}{establishmentName}</span></div>}
            onCancel={() => setModalVisible(false)} footer={null} width="calc(100vw - 64px)"
            style={{ ...workspaceStyle, maxWidth: 1440 }}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}
              initialValues={{ payment_date: dayjs(), amount: 0 }}>
              <div className="payment-entry-modal__body">
                <BusinessResourceNotice resource={formChoices} label="listes de choix" />
                <BusinessResourceNotice resource={balances} label="commandes et soldes" />
                {unpaidOrders.some((row) => row.selected && Number(row.payment_amount) > Number(row.due_amount) + 0.0001) &&
                  <Alert type="warning" showIcon message="Le solde de certaines commandes a changé."
                    description="Les affectations sont conservées. Vérifiez les montants avant d'enregistrer." />}
                <div className="payment-entry-modal__fields">
                  <Form.Item name="supplier_id" label="Fournisseur" className="payment-entry-modal__client"
                    rules={[{ required: true, message: "Veuillez sélectionner un fournisseur" }]}>
                    <Select placeholder="Sélectionner un fournisseur" showSearch optionFilterProp="children"
                      onChange={handleFormSupplierChange} disabled={editMode}>
                      {formEntities.map((supplier) => <Option key={supplier.ID_Frs} value={supplier.USER_ID}>{supplier.Nom_Raison_Sociale}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="payment_date" label="Date de paiement" className="payment-entry-modal__date"
                    rules={[{ required: true, message: "Veuillez sélectionner une date" }]}>
                    <DatePicker format="DD/MM/YYYY" />
                  </Form.Item>
                  <Form.Item name="amount" label="Montant versé" className="payment-entry-modal__amount"
                    rules={[{ required: true, message: "Veuillez saisir un montant" }]}>
                    <InputNumber {...amountInputProps} min={0} addonAfter="FCFA" />
                  </Form.Item>
                  <Form.Item name="payment_mode_id" label="Mode de paiement" className="payment-entry-modal__mode"
                    rules={[{ required: true, message: "Veuillez sélectionner un mode de paiement" }]}>
                    <Select onChange={suggestTreasuryAccount} placeholder="Sélectionner un mode de paiement"
                      loading={loadingPaymentModes} disabled={!currentWarehouseId || loadingPaymentModes}
                      showSearch optionFilterProp="children"
                      notFoundContent={loadingPaymentModes ? <Spin size="small" /> : "Aucun mode de paiement trouvé"}>
                      {paymentModes.map((mode) => <Option key={mode.id} value={mode.id}>{mode.name}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="treasury_account_id" label="Compte de trésorerie" className="payment-entry-modal__account"
                    rules={[{ required: true, message: "Veuillez sélectionner un compte de trésorerie" }]}
                    extra={!formEntityId ? "Sélectionnez un fournisseur pour vérifier ses commandes avant l’enregistrement." : undefined}>
                    <Select placeholder="Sélectionner un compte de trésorerie" loading={accountsLoading}
                      disabled={accountsLoading || treasuryAccounts.length === 0 || balances.loading || !balances.data}>
                      {treasuryAccounts.map((account: any) => <Option key={account.id} value={account.id}>{account.name}</Option>)}
                    </Select>
                  </Form.Item>
                </div>
                {formChoices.status === "verified" && treasuryAccounts.length === 0 && <Alert type="warning" showIcon
                  message="Aucun compte de trésorerie disponible"
                  description="Configurez un compte commercial actif pour cet établissement avant d'enregistrer un paiement." />}

                <section className="payment-entry-modal__orders" aria-label="Commandes à payer">
                  <div className="payment-entry-modal__orders-heading">
                    <h4>Commandes à payer{currentSupplierName ? " · " + currentSupplierName : ""}</h4>
                    <Space wrap><Text type="secondary">{selectedOrderCount} sélectionnée{selectedOrderCount > 1 ? "s" : ""}</Text>
                      {formEntityId && <Button type="link" icon={<ReloadOutlined />} loading={balances.loading}
                        aria-label="Actualiser les commandes" onClick={() => void balances.refresh().catch(() => {})}>Actualiser</Button>}
                    </Space>
                  </div>
                  {unpaidOrders.length > 0 ? <Table columns={orderColumns} dataSource={unpaidOrders}
                    rowKey="id" pagination={false} size="small" tableLayout="fixed" scroll={{ y: 280 }}
                    rowClassName={(order) => order.selected ? "payment-entry-modal__order--selected" : ""} />
                    : formEntityId && balances.status === "verified" ? <Alert type="info" showIcon
                      message="Aucune commande à payer"
                      description="Ce fournisseur n'a aucune commande non payée ou partiellement payée en attente de règlement. Vous pouvez enregistrer un paiement sans affectation après confirmation." />
                    : <div className="payment-entry-modal__empty" aria-busy={balances.loading}>
                      {balances.loading ? <Spin tip="Lecture des commandes…" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={formEntityId ? "Les commandes sont indisponibles. Réessayez leur actualisation." : "Sélectionnez un fournisseur pour afficher ses commandes à payer."} />}
                    </div>}
                </section>

                <Collapse className="payment-entry-modal__remarks" activeKey={remarksExpanded ? ["notes"] : []}
                  onChange={(keys) => setRemarksExpanded(keys.includes("notes"))}
                  items={[{ key: "notes", label: "Ajouter une remarque (facultatif)", forceRender: true,
                    children: <Form.Item name="notes" label="Remarques"><Input.TextArea rows={3} /></Form.Item> }]} />

                <section className="payment-entry-modal__totals" aria-label="Récapitulatif du paiement" aria-live="polite">
                  <div><span>Versé</span><strong>{money(disbursedAmount)}</strong></div>
                  <div><span>Affecté</span><strong>{money(allocatedAmount)}</strong></div>
                  <div className={disbursedAmount > 0 && Math.abs(allocationGap) <= 1
                    ? "payment-entry-modal__gap--matched" : "payment-entry-modal__gap--check"}>
                    {disbursedAmount > 0 && Math.abs(allocationGap) <= 1 ? <CheckCircleFilled /> : <InfoCircleOutlined />}
                    <div><span>Écart</span><strong>{money(allocationGap)}</strong></div>
                  </div>
                  <p><InfoCircleOutlined /> Les soldes seront revérifiés à l’enregistrement.</p>
                </section>
              </div>
              <footer className="payment-entry-modal__footer">
                <Text type="secondary">{selectedOrderCount} commande{selectedOrderCount > 1 ? "s" : ""} sélectionnée{selectedOrderCount > 1 ? "s" : ""}</Text>
                <Space wrap><Button onClick={() => setModalVisible(false)}>Annuler</Button>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined aria-hidden="true" />} loading={loading}
                    aria-label={editMode ? "Mettre à jour le paiement" : "Enregistrer le paiement"}
                    disabled={accountsLoading || treasuryAccounts.length === 0 || balances.loading || !balances.data}>
                    {editMode ? "Mettre à jour le paiement" : "Enregistrer le paiement"}</Button>
                </Space>
              </footer>
            </Form>
          </Modal>

          {/* Modal de détail du paiement */}
          <Modal
            title="Détail du paiement"
            open={detailModalVisible}
            onCancel={() => setDetailModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setDetailModalVisible(false)}>
                Fermer
              </Button>,
            ]}
            width="95%"
            style={{ maxWidth: 700, top: 20 }}
          >
            {currentPayment ? (
              <div>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card size="small" title="Informations générales">
                      <p>
                        <FileTextOutlined /> <strong>No Transaction:</strong>{" "}
                        {currentPayment.payment_number}
                      </p>
                      <p>
                        <CalendarOutlined /> <strong>Date:</strong>{" "}
                        {dayjs(currentPayment.date).format("DD/MM/YYYY")}
                      </p>
                      <p>
                        <UserOutlined /> <strong>Fournisseur:</strong>{" "}
                        {currentPayment.entity_name}
                      </p>
                      <p>
                        <BankOutlined /> <strong>Mode de paiement:</strong>{" "}
                        {currentPayment.payment_mode_name}
                      </p>
                      <p>
                        <strong>Montant:</strong>{" "}
                        {currentPayment.amount.toLocaleString("fr-FR", {
                          style: "currency",
                          currency: "XOF",
                        })}
                      </p>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Informations complémentaires">
                      <p>
                        <strong>Établissement:</strong>{" "}
                        {currentPayment.warehouse_name}
                      </p>
                      <p>
                        <strong>Entreprise:</strong>{" "}
                        {currentPayment.company_name}
                      </p>
                      <p>
                        <strong>Remarques:</strong>{" "}
                        {currentPayment.notes || "Aucune"}
                      </p>
                      {currentPayment.online_order_id && (
                        <Alert
                          type="info"
                          showIcon
                          icon={<LockOutlined />}
                          message={`Remboursement de la commande e-commerce ${currentPayment.ecommerce_order_number}`}
                          description="Cette écriture inverse est protégée par la piste d'audit de la commande en ligne."
                        />
                      )}
                    </Card>
                  </Col>
                </Row>

                {currentPayment.orders && currentPayment.orders.length > 0 && (
                  <Card
                    size="small"
                    title="Commandes associées"
                    style={{ marginTop: 16 }}
                  >
                    <div className="overflow-x-auto">
                      <Table
                        dataSource={currentPayment.orders}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        columns={[
                          {
                            title: "No Facture",
                            dataIndex: "invoice_number",
                            key: "invoice_number",
                          },
                          {
                            title: "Montant total",
                            dataIndex: "order_total",
                            key: "order_total",
                            render: (text: number | undefined) =>
                              typeof text === "number"
                                ? text.toLocaleString("fr-FR", {
                                    style: "currency",
                                    currency: "XOF",
                                  })
                                : "N/A",
                            responsive: ["md"],
                          },
                          {
                            title: "Montant payé",
                            dataIndex: "amount_applied",
                            key: "amount_applied",
                            render: (text: number | undefined) =>
                              typeof text === "number"
                                ? text.toLocaleString("fr-FR", {
                                    style: "currency",
                                    currency: "XOF",
                                  })
                                : "N/A",
                          },
                          {
                            title: "Statut",
                            dataIndex: "payment_status",
                            key: "payment_status",
                            responsive: ["sm"],
                          },
                        ]}
                      />
                    </div>
                  </Card>
                )}

                {currentPayment.ecommerce_orders &&
                  currentPayment.ecommerce_orders.length > 0 && (
                    <Card
                      size="small"
                      title="Commande e-commerce associée"
                      style={{ marginTop: 16 }}
                    >
                      {currentPayment.ecommerce_orders.map((link: any) => (
                        <p key={`${link.online_order_id}-${link.event_type}`}>
                          <strong>{link.order_number}</strong> ·{" "}
                          {link.event_type === "refund"
                            ? "Remboursement"
                            : "Encaissement"}
                        </p>
                      ))}
                    </Card>
                  )}
              </div>
            ) : (
              <Spin />
            )}
          </Modal>
        </div>
      </Content>
    </Layout>
  );
};

export default PaymentSortant;
